import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dealsRepo, isSupabaseConfigured } from "@/lib/db";
import type { Deal } from "@/types";
import { ConfigBanner } from "@/components/ConfigBanner";

export function DealsPage(): JSX.Element {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    let cancelled = false;
    setLoading(true);
    dealsRepo
      .list()
      .then((rows) => {
        if (!cancelled) setDeals(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <ConfigBanner />
      <section className="panel">
        <h2>딜 목록</h2>
        <p className="sub">
          선적=딜=마스터파일. status가 진행의 단일 진실 소스.{" "}
          <Link to="/deals/new">+ 새 딜 만들기</Link>
        </p>
        {!isSupabaseConfigured() && (
          <p className="muted">
            Supabase 미설정 상태입니다. 환경변수를 채우면 딜 목록이 표시됩니다.
          </p>
        )}
        {error && <p style={{ color: "var(--block)" }}>{error}</p>}
        {loading && <p className="muted">불러오는 중…</p>}
        {isSupabaseConfigured() && !loading && !error && deals.length > 0 && (
          <p className="sub">
            {Object.entries(
              deals.reduce<Record<string, number>>((acc, d) => {
                if (d.status === "done" || d.status === "cancelled") return acc;
                const key = `${d.destination_country} · ${d.status}`;
                acc[key] = (acc[key] ?? 0) + 1;
                return acc;
              }, {}),
            ).map(([key, n]) => (
              <span key={key} className="badge INFO" style={{ marginRight: 6 }}>
                {key} × {n}
              </span>
            ))}
          </p>
        )}
        {isSupabaseConfigured() && !loading && !error && (
          <table className="findings">
            <thead>
              <tr>
                <th>Deal No.</th>
                <th>제목</th>
                <th>목적국</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              {deals.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    딜이 없습니다.
                  </td>
                </tr>
              )}
              {deals.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link to={`/deals/${d.id}`}>
                      <code>{d.deal_no}</code>
                    </Link>
                  </td>
                  <td>
                    <Link to={`/deals/${d.id}`}>{d.title ?? "—"}</Link>
                  </td>
                  <td>{d.destination_country}</td>
                  <td>{d.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
